import CustomButton from '@/components/Returns/CustomButton'
import CustomMultiSelectBase from '@/components/Returns/CustomMultiSelect'
import { MTCustomSelect } from '@/multi-territory/mt-components/mt-shared/MTCustomSelect'
import { runWithMTNavigationGuard } from '@/multi-territory/mt-utils/mt-navigation-guard'
import {
  fetchMTEntityListIfNeeded,
  fetchMTPeriodOptionsIfNeeded,
  fetchMTTanByEntityIfNeeded,
} from '@/store/actions/mtEntityTanActions'
import {
  setMTSelectedEntity,
  setMTSelectedMonths,
  setMTSelectedTan,
  setMTSelectedYear,
  triggerMTView,
} from '@/store/slice/mtEntityTanSlice'
import { ADMIN } from '@/utils/role-capabilities'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

const CustomSelect = MTCustomSelect as any
const CustomMultiSelect = CustomMultiSelectBase as any

// --- Types ---

export type MTKeyFilterViewData = {
  entityUuid: string
  entityName: string
  tanUuid: string
  tanNumber: string
  tanApplicable: boolean
  uniqueIdentificationNumber: string
  tanMasterUuid: string | null
  entityMasterUuid: string | null
  /** Selected financial/calendar year e.g. "2024-25" or "2025" */
  year: string
  /** Selected period filters — array of { year, month } objects */
  months: { year: number; month: number }[]
}

type MTKeyFilterPanelProps = {
  onViewClick: (filterData: MTKeyFilterViewData) => void
  showYear?: boolean
  showMonth?: boolean
  actionLabel?: string
  actionDisabled?: boolean
  viewMode?: boolean
  onGoBack?: () => void
  /** When in viewMode, show these plain-text values in the Entity/TAN dropdowns. */
  viewModeLabels?: { entityName?: string; tanOrUin?: string }
  children?: React.ReactNode
}

// ─── Component ────────────────────────────────────────────────────────────────

export function MTKeyFilterPanel({
  onViewClick,
  showYear = false,
  showMonth = false,
  actionLabel = 'View',
  actionDisabled = false,
  viewMode = false,
  onGoBack,
  viewModeLabels,
  children,
}: MTKeyFilterPanelProps) {
  const dispatch = useDispatch()

  // ── Redux ──
  const {
    entityList,
    tanListByEntity,
    entityMetaByUuid,
    selectedEntityUuid,
    selectedTanUuid,
    selectedYear: reduxYear,
    selectedMonths: reduxMonths,
    entityLoading,
    tanLoading,
    tanError,
    periodOptions,
    periodOptionsLoading,
  } = useSelector((state: any) => state.mtEntityTan)

  const countryCode = useSelector(
    (state: { country: { countryCode: string } }) => state.country?.countryCode
  )
  const userRole = useSelector((state: any) => state.user.role)
  const isRoleDisabled = userRole === ADMIN

  // ── Local state for year / month ──
  const [selectedYear, setSelectedYear] = useState(reduxYear || '')
  const [selectedMonths, setSelectedMonths] = useState<string[]>(
    reduxMonths || []
  ) // JSON strings

  // ── Collapsible filter panel ──
  const [filtersExpanded, setFiltersExpanded] = useState(true)

  // ── Entity meta ──
  const selectedEntityMeta = useMemo(() => {
    if (!selectedEntityUuid || !entityMetaByUuid) return null
    return entityMetaByUuid[selectedEntityUuid] || null
  }, [entityMetaByUuid, selectedEntityUuid])

  const tanApplicable = selectedEntityMeta?.tanApplicable ?? true
  const uniqueIdentificationNumber =
    selectedEntityMeta?.uniqueIdentificationNumber || ''

  // ── Year / Month options from API ──
  const yearOptions = useMemo(() => {
    if (!periodOptions?.yearLabels) return []
    return periodOptions.yearLabels.map((label: string, i: number) => ({
      label,
      value: label,
      year: periodOptions.years[i],
    }))
  }, [periodOptions])

  const monthOptions = useMemo(() => {
    if (!selectedYear || !periodOptions?.months) return []
    const selectedYearNum =
      periodOptions.years[periodOptions.yearLabels.indexOf(selectedYear)]
    return periodOptions.months.map((m: { value: number; label: string }) => ({
      label: m.label,
      value: JSON.stringify({ year: selectedYearNum, month: m.value }),
    }))
  }, [selectedYear, periodOptions])

  // ── Fetch entity list + period options ──
  // `periodOptions` is a dependency so that when it is invalidated (set to null
  // after a data upload), this effect re-runs and re-fetches the latest years.
  useEffect(() => {
    if (!countryCode) return
    dispatch(fetchMTEntityListIfNeeded(countryCode) as any)
    if (showYear) {
      dispatch(fetchMTPeriodOptionsIfNeeded(countryCode) as any)
    }
  }, [dispatch, countryCode, showYear, periodOptions])

  // ── Reset local year/month when country changes ──
  const prevCountryRef = useRef(countryCode)
  useEffect(() => {
    if (prevCountryRef.current && prevCountryRef.current !== countryCode) {
      setSelectedYear('')
      setSelectedMonths([])
    }
    prevCountryRef.current = countryCode
  }, [countryCode])

  // ── Sync Redux year/months into local state when they change externally ──
  useEffect(() => {
    setSelectedYear(reduxYear || '')
  }, [reduxYear])
  useEffect(() => {
    setSelectedMonths(reduxMonths || [])
  }, [reduxMonths])

  // ── Fetch TAN on entity change ──
  useEffect(() => {
    if (!selectedEntityUuid) return
    dispatch(fetchMTTanByEntityIfNeeded(selectedEntityUuid, countryCode) as any)
  }, [selectedEntityUuid]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Entity options ──
  const entityOptions = useMemo(() => {
    if (!entityList || !Array.isArray(entityList)) return []
    return entityList
      .filter((e: any) => !countryCode || e.countryCode === countryCode)
      .map((e: any) => ({ label: e.entityName, value: e.uuid }))
  }, [entityList, countryCode])

  // ── TAN options ──
  const tanOptions = useMemo(() => {
    const tanRecords = tanListByEntity[selectedEntityUuid] || []
    return tanRecords.map((t: any) => ({
      label: t.attributes?.tax_identification_number || t.uuid,
      value: t.uuid,
    }))
  }, [tanListByEntity, selectedEntityUuid])

  // ── Handlers ──

  // Changing Entity / TIN resets the loaded table, so it is guarded like View
  // (screens in edit mode show the unsaved-changes confirmation).
  const handleEntityChange = useCallback(
    (e: { target: { value: string } }) => {
      const value = e.target.value
      runWithMTNavigationGuard(() => {
        dispatch(setMTSelectedEntity(value))
        dispatch(setMTSelectedYear(''))
        dispatch(setMTSelectedMonths([]))
        setSelectedYear('')
        setSelectedMonths([])
      })
    },
    [dispatch]
  )

  const handleTanChange = useCallback(
    (e: { target: { value: string } }) => {
      const value = e.target.value
      runWithMTNavigationGuard(() => {
        dispatch(setMTSelectedTan(value))
        dispatch(setMTSelectedYear(''))
        dispatch(setMTSelectedMonths([]))
        setSelectedYear('')
        setSelectedMonths([])
      })
    },
    [dispatch]
  )

  const handleYearChange = (e: { target: { value: string } }) => {
    setSelectedYear(e.target.value)
    setSelectedMonths([])
    dispatch(setMTSelectedYear(e.target.value))
  }

  const handleMonthChange = (e: { target: { value: string[] } }) => {
    setSelectedMonths(e.target.value)
    dispatch(setMTSelectedMonths(e.target.value))
  }

  const runViewClick = useCallback(() => {
    dispatch(triggerMTView())
    const entity = entityOptions.find(
      (e: any) => e.value === selectedEntityUuid
    )
    const tan = tanOptions.find((t: any) => t.value === selectedTanUuid)

    const parsedMonths: { year: number; month: number }[] = selectedMonths
      .map(v => {
        try {
          return JSON.parse(v)
        } catch {
          return null
        }
      })
      .filter(Boolean)

    onViewClick({
      entityUuid: selectedEntityUuid,
      entityName: entity?.label ?? '',
      tanUuid: tanApplicable ? selectedTanUuid : '',
      tanNumber: tanApplicable
        ? (tan?.label ?? '')
        : uniqueIdentificationNumber,
      tanApplicable,
      uniqueIdentificationNumber,
      tanMasterUuid: tanApplicable ? selectedTanUuid : null,
      entityMasterUuid: tanApplicable ? null : selectedEntityUuid,
      year: selectedYear,
      months: parsedMonths,
    })
  }, [
    dispatch,
    selectedEntityUuid,
    selectedTanUuid,
    entityOptions,
    tanOptions,
    tanApplicable,
    uniqueIdentificationNumber,
    selectedYear,
    selectedMonths,
    onViewClick,
  ])

  // Reloading the view discards inline edits — let the active screen confirm
  const handleViewClick = useCallback(
    () => runWithMTNavigationGuard(runViewClick),
    [runViewClick]
  )

  // NOTE: Removed auto-trigger of View when tanApplicable=false.
  // User must always click View button explicitly to load the table.

  // ── Disabled logic ──
  const tanSelected = tanApplicable ? !!selectedTanUuid : true
  const isViewDisabled =
    !selectedEntityUuid ||
    !tanSelected ||
    (showYear && !selectedYear) ||
    (showMonth && selectedMonths.length === 0)

  const tanPlaceholder = useMemo(() => {
    if (!selectedEntityUuid) return 'Select TIN'
    if (tanLoading) return 'Loading...'
    if (tanError) return 'Failed to load TAN'
    if (tanOptions.length === 0) return 'No TAN found'
    return 'Select TIN'
  }, [selectedEntityUuid, tanLoading, tanError, tanOptions])

  // ── Render ──

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 0,
        backgroundColor: '#fff',
        borderBottom: '1px solid var(--border-subtel)',
      }}
    >
      {/* Collapsible header — "Filter ▼" */}
      <Box
        onClick={() => setFiltersExpanded(v => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          px: 2,
          py: '10px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        {viewMode && onGoBack && (
          <Tooltip title="Go Back">
            <IconButton
              size="small"
              aria-label="Go back to the returns list"
              onClick={e => {
                e.stopPropagation()
                onGoBack()
              }}
              sx={{ color: 'var(--forbrand-color)', p: 0.25 }}
            >
              <KeyboardArrowLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Typography
          sx={{ fontSize: '15px', fontWeight: 700, color: '#1a1a1a' }}
        >
          Filters
        </Typography>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 18,
            color: '#1a1a1a',
            transition: 'transform 0.2s',
            transform: filtersExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
          }}
        />
      </Box>

      {/* Collapsible filter row */}
      <Collapse in={filtersExpanded}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '12px',
            flexWrap: 'wrap',
            px: 2,
            py: '16px',
          }}
        >
          {/* Entity */}
          <CustomSelect
            value={
              viewMode && viewModeLabels?.entityName
                ? '__view__'
                : selectedEntityUuid || ''
            }
            onChange={handleEntityChange}
            options={
              viewMode && viewModeLabels?.entityName
                ? [{ label: viewModeLabels.entityName, value: '__view__' }]
                : entityOptions
            }
            getOptionLabel={(opt: any) => opt.label}
            getOptionValue={(opt: any) => opt.value}
            placeholder={entityLoading ? 'Loading...' : 'Select Entity Name'}
            disabled={entityLoading || isRoleDisabled || viewMode}
            MenuProps={{
              PaperProps: { sx: { maxHeight: '250px', width: '225px' } },
              MenuListProps: {
                sx: {
                  '& .MuiMenuItem-root': {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  },
                },
              },
            }}
          />

          {/* TAN / UIN */}
          <CustomSelect
            value={
              viewMode && viewModeLabels?.tanOrUin
                ? '__view__'
                : tanApplicable
                  ? selectedTanUuid || ''
                  : 'uin'
            }
            onChange={handleTanChange}
            options={
              viewMode && viewModeLabels?.tanOrUin
                ? [{ label: viewModeLabels.tanOrUin, value: '__view__' }]
                : tanApplicable
                  ? tanOptions
                  : [
                      {
                        label: uniqueIdentificationNumber || '\u2014',
                        value: 'uin',
                      },
                    ]
            }
            getOptionLabel={(opt: any) => opt.label}
            getOptionValue={(opt: any) => opt.value}
            placeholder={tanPlaceholder}
            disabled={
              !tanApplicable ||
              !selectedEntityUuid ||
              tanOptions.length === 0 ||
              tanLoading ||
              isRoleDisabled ||
              viewMode
            }
            MenuProps={{
              PaperProps: { sx: { maxHeight: '250px', width: '225px' } },
              MenuListProps: {
                sx: {
                  '& .MuiMenuItem-root': {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  },
                },
              },
            }}
          />

          {/* Year — single select */}
          {showYear && (
            <CustomSelect
              value={selectedYear}
              onChange={handleYearChange}
              options={yearOptions}
              getOptionLabel={(opt: any) => opt.label}
              getOptionValue={(opt: any) => opt.value}
              placeholder={periodOptionsLoading ? 'Loading...' : 'Select Year'}
              disabled={
                !selectedEntityUuid ||
                (tanApplicable && !selectedTanUuid) ||
                periodOptionsLoading ||
                viewMode
              }
            />
          )}

          {/* Month — multi-select */}
          {showMonth && (
            <CustomMultiSelect
              value={selectedMonths}
              onChange={handleMonthChange}
              options={monthOptions}
              getOptionLabel={(opt: any) => opt.label}
              getOptionValue={(opt: any) => opt.value}
              placeholder="Select Month"
              disabled={!selectedYear || isRoleDisabled || viewMode}
            />
          )}

          {/* Slot for extra filters (e.g. status) */}
          {children}

          {/* Action button (label varies by screen: View / Generate) */}
          <CustomButton
            onClick={handleViewClick}
            disabled={
              isViewDisabled || isRoleDisabled || actionDisabled || viewMode
            }
          >
            {actionLabel}
          </CustomButton>
        </Box>
      </Collapse>
    </Paper>
  )
}

/**
 * @deprecated Use MTKeyFilterPanel instead. Kept for backward compatibility.
 */
export const MTDefineKeyFilter = MTKeyFilterPanel
